import React, {
	FunctionComponent,
	useCallback,
	useEffect,
	useState,
	createContext,
	useContext,
	useRef,
} from 'react';
import { IScanResults, SpeedTestStatus, WebScanState } from '../models';
import { SpeedTestEmitterKey, SpeedtestType } from '../enums';
import { Ndt7 } from '../utils';

export type NetworkTestContextType = {
	startSpeedTest: () => void;
	cancelSpeedTest: () => void;
	downloadSpeed: string;
	scanStatus: SpeedTestStatus;
	webScanState: WebScanState;
};

const defaultNetworkTestContext: NetworkTestContextType = {
	startSpeedTest: undefined,
	cancelSpeedTest: undefined,
	downloadSpeed: undefined,
	scanStatus: SpeedTestStatus.none,
	webScanState: WebScanState.NOT_STARTED,
};

const NetworkTestContext = createContext<NetworkTestContextType>(
	defaultNetworkTestContext
);

export const useNetworkTestContext = () => useContext(NetworkTestContext);

type NetworkTestProviderProps = {
	onScanComplete?: (results: IScanResults) => void;
	onScanStart?: () => void;
	onScanCancel?: () => void;
};

export const NetworkTestProvider: FunctionComponent<NetworkTestProviderProps> = ({
	onScanComplete,
	onScanStart,
	onScanCancel,
	children,
}) => {
	const { current: speedTest } = useRef(new Ndt7(SpeedtestType.DOWNLOAD));

	const [webScanState, setWebScanState] = useState<WebScanState>(
		WebScanState.NOT_STARTED
	);

	const [downloadSpeed, setDownloadSpeed] = useState<string>('N/A');
	const [speedTestStatus, setSpeedTestStatus] = useState<SpeedTestStatus>(
		SpeedTestStatus.none
	);

	useEffect(() => {
		if (!speedTest) return;

		speedTest.emitter.on(SpeedTestEmitterKey.STATUS, (status: SpeedTestStatus) => {
			console.log('Speed test status:', status);
			setSpeedTestStatus(status);

			if (status === SpeedTestStatus.completed) {
				setWebScanState(WebScanState.FINISHED);
			}
		});

		speedTest.emitter.on(SpeedTestEmitterKey.DOWNLOAD_SPEED, (speed: string) => {
			console.log('Download speed:', speed);
			setDownloadSpeed(speed);
		});

		return () => {
			speedTest.emitter.offAllEvents();
		};
	}, [speedTest]);

	const startSpeedTest = async () => {
		setWebScanState(WebScanState.INITIALIZING);
		await speedTest.scan();
		onScanStart?.();
		setWebScanState(WebScanState.STARTED);
	};

	useEffect(() => {
		if (webScanState === WebScanState.FINISHED) {
			onScanComplete?.({ downloadSpeed });
		}
	}, [downloadSpeed, onScanComplete, webScanState]);

	const cancelSpeedTest = useCallback(() => {
		setWebScanState(WebScanState.NOT_STARTED);
		onScanCancel?.();
		speedTest.cancel();
		setSpeedTestStatus(SpeedTestStatus.canceled);
	}, [onScanCancel, speedTest]);

	const contextValues: NetworkTestContextType = {
		startSpeedTest,
		cancelSpeedTest,
		scanStatus: speedTestStatus,
		downloadSpeed,
		webScanState,
	};

	return (
		<NetworkTestContext.Provider value={contextValues}>
			{children}
		</NetworkTestContext.Provider>
	);
};
